import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from "react-i18next";

interface MagicLinkPageProps {
  onLogin: (token: string, playerId?: string) => void;
}

type Status = "loading" | "success" | "error";

export default function MagicLinkPage({ onLogin }: MagicLinkPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>("loading");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // évite le double-appel (token usage unique)
    ran.current = true;

    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      setStatus("error");
      return;
    }

    (async () => {
      try {
        const res = await apiRequest("POST", "/api/auth/magic", { token });
        const data = await res.json();
        if (data.token) {
          onLogin(data.token, data.playerId);
          setStatus("success");
          setTimeout(() => { window.location.href = "/"; }, 800);
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    })();
  }, [onLogin]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="py-10 text-center space-y-4">
          {status === "loading" && (
            <>
              <Loader2 className="h-10 w-10 animate-spin text-padel-green mx-auto" />
              <p className="text-gray-600">{t('auth.magicVerifying', 'Connexion en cours...')}</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
              <p className="font-semibold">{t('auth.magicSuccess', 'Connecté ! Redirection...')}</p>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="h-12 w-12 text-red-500 mx-auto" />
              <h3 className="font-semibold text-lg">{t('auth.magicErrorTitle', 'Lien invalide ou expiré')}</h3>
              <p className="text-sm text-gray-600">
                {t('auth.magicErrorDesc', 'Ce lien de connexion n\'est plus valable. Demande-en un nouveau depuis la page de connexion.')}
              </p>
              <Button className="mt-2" onClick={() => { window.location.href = "/"; }}>
                {t('auth.backToLogin', 'Retour à la connexion')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
